import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { mutate } from "swr";
import { gaVisitorId } from "@/components/util";
import { executeRecaptchaWithCallback } from "@/utils/recaptcha";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Alert, AlertDescription } from "@/components/ui/Alert";

export default function UpdateEmailForm({
  open,
  setOpen,
  currentEmail,
}: {
  open: boolean;
  setOpen: (open: boolean) => any;
  currentEmail: string;
}) {
  const router = useRouter();

  const [newEmail, setNewEmail] = useState("");
  const [emailConfirmation, setEmailConfirmation] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [showOtp, setShowOtp] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const requestOtp = useCallback(() => {
    if (newEmail !== emailConfirmation) {
      setError("Confirmation does not match email.");
      return;
    }

    if (newEmail === currentEmail) {
      setError("New email cannot be the same as your current email.");
      return;
    }

    // Request OTP code from the server
    executeRecaptchaWithCallback(
      "update_email",
      async (recaptcha) => {
        const params = {
          email: newEmail,
          recaptcha,
          vid: gaVisitorId(),
        };

        setError("");
        try {
          const resp = await fetch("/web_api/accounts/request_email_change", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(params),
          });
          const json = await resp.json();

          if (json.status === "ok") {
            setShowOtp(true);
            setSuccessMessage("Verification code has been sent to your new email address.");
          } else {
            setError(json.error || "Error. Try again later.");
          }
        } catch (e) {
          setError("Failed to request verification code. Please try again later.");
        }
      },
      (error) => {
        setError("Error with captcha verification. Please try again.");
      },
    );
  }, [newEmail, emailConfirmation, currentEmail]);

  const verifyOtp = useCallback(() => {
    if (!otpCode) {
      setError("Please enter the verification code.");
      return;
    }

    // Verify OTP and complete email change
    executeRecaptchaWithCallback(
      "verify_email_change",
      async (recaptcha) => {
        const params = {
          email: newEmail,
          otp_code: otpCode,
          recaptcha,
          vid: gaVisitorId(),
        };

        setError("");
        try {
          const resp = await fetch("/web_api/accounts/verify_email_change", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(params),
          });
          const json = await resp.json();

          if (json.status === "ok") {
            setSuccessMessage("Your email has been updated successfully.");
            setTimeout(() => {
              setSuccessMessage("");
              mutate("/web_api/accounts/current");
              router.push("/dashboard/settings");
              setOpen(false);
            }, 2000);
          } else {
            setError(json.error || "Error. Try again later.");
          }
        } catch (e) {
          setError("Failed to verify code. Please try again later.");
        }
      },
      (error) => {
        setError("Error with captcha verification. Please try again.");
      },
    );
  }, [newEmail, otpCode, router, setOpen]);

  const onFormSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      if (showOtp) {
        verifyOtp();
      } else {
        requestOtp();
      }
    },
    [showOtp, requestOtp, verifyOtp],
  );

  // Reset form when opening/closing
  const resetForm = useCallback(() => {
    setNewEmail("");
    setEmailConfirmation("");
    setOtpCode("");
    setShowOtp(false);
    setError("");
    setSuccessMessage("");
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    setOpen(false);
  }, [resetForm, setOpen]);

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogHeader onClose={handleClose}>
        <DialogTitle>Update Email Address</DialogTitle>
        <DialogDescription>
          {showOtp
            ? "Enter the verification code sent to your new email"
            : `Current email: ${currentEmail}`}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onFormSubmit}>
        <DialogBody>
          {/* Message display area */}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {successMessage && (
            <Alert className="mb-4">
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          {!showOtp && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">New Email</label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  placeholder="Enter new email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confirm New Email
                </label>
                <Input
                  type="email"
                  value={emailConfirmation}
                  onChange={(e) => setEmailConfirmation(e.target.value)}
                  required
                  placeholder="Re-enter new email address"
                />
              </div>
            </div>
          )}

          {showOtp && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Verification Code
              </label>
              <Input
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder="Enter code sent to your new email"
                required
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit">{showOtp ? "Verify Code" : "Continue"}</Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
